/**
 * 404-guard for the four `React.lazy` route wrappers (task 106 §1.3, decision 3
 * "Вариант A"). After a deploy the old content-hashed chunks disappear, so a
 * lazy `import()` on a stale tab throws a module-not-found error → white screen.
 *
 * The routes that use `lazyRouteComponent` already get this guard for free
 * (TanStack reloads once on `isModuleNotFoundError`, TanStack #6107). The prop-
 * injecting wrappers (ProjectDetail / UnitDetail / TemplateEditor / Approval-
 * Detail) go through `React.lazy`, which the router's `defaultErrorComponent`
 * does NOT cover — so they need this boundary.
 *
 * On a chunk error we reload exactly once (navigations are network-first, so
 * the reload pulls a fresh index.html with valid hashes). A `sessionStorage`
 * flag — the same scheme TanStack's own `lazyRouteComponent` uses — prevents a
 * reload loop if the chunk is genuinely gone. Non-chunk errors are re-thrown so
 * the router's error component still handles real page failures.
 */
import { Component, type ReactNode } from "react";
import { isModuleNotFoundError } from "@tanstack/router-core";
import { RouteFallback } from "@/layout/RouteFallback";

const RELOAD_KEY_PREFIX = "tanstack_router_reload:";

/**
 * Decides whether a caught error is a chunk-load failure that warrants a
 * one-time reload, recording the flag so the next occurrence does not loop.
 * Pure and storage-injectable so the reload policy is unit-testable without
 * touching `window.location`.
 */
export function shouldReloadForChunkError(
  error: unknown,
  storage: Pick<Storage, "getItem" | "setItem"> = sessionStorage,
): boolean {
  if (!isModuleNotFoundError(error) || !(error instanceof Error)) {
    return false;
  }
  const key = RELOAD_KEY_PREFIX + error.message;
  if (storage.getItem(key)) {
    return false;
  }
  storage.setItem(key, "1");
  return true;
}

interface Props {
  children: ReactNode;
}

interface State {
  error: unknown;
}

export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  componentDidCatch(error: unknown): void {
    if (shouldReloadForChunkError(error)) {
      window.location.reload();
    }
  }

  render(): ReactNode {
    const { error } = this.state;
    if (error) {
      // Chunk error: a reload is in flight (or the loop-guard tripped) — show the
      // calm brand-mark fallback rather than a crash.
      if (isModuleNotFoundError(error)) {
        return <RouteFallback />;
      }
      // Real page error — let the router's error component handle it.
      throw error;
    }
    return this.props.children;
  }
}
